import { getCourbePatient } from '@/app/(app)/(stack)/patients/[detail]/(tab)/implants';
import { useUserContext } from '@/components/Auth/UserContext';
import CenteredLoading from '@/components/CenteredLoading';
import { usePatientContext } from '@/components/Contexts/PatientContext';
import Pdf from '@/components/Pdf/Pdf';
import { getHabitudeChartOptions } from '@/components/ScreenComponents/HabitudesChart';
import { getImplantChartOptions } from '@/components/ScreenComponents/ImplantsChart';
import SimpleLink from '@/components/SimpleLink';
import parametersData from '@/data/parametersData';
import TemplateRapport_EN from '@/templates/TemplateRapport_EN';
import TemplateRapport_FR from '@/templates/TemplateRapport_FR';
import DateUtils from '@/utils/DateUtils';
import EChartsCaptureComponent from '@/utils/EChartsBase64';
import pdfGenerator from '@/utils/pdfGenerator/pdfGenerator';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sharing from 'expo-sharing';
import * as Print from 'expo-print';
import * as FileSystem from 'expo-file-system/legacy';
import { View } from 'react-native';
import { getTitleForLanguage, useUserTitle } from '@/hooks/useUserTitle';

const RapportPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUserContext();
  const { patient } = usePatientContext();
  const { titles: userTitles } = useUserTitle(user?.id);
  const [generating, setGenerating] = useState(true);
  const [pdfData, setPdfData] = useState<string>();
  const [currentLayout, setCurrentLayout] = useState<
    { height: number; width: number } | undefined
  >();

  const [courbePng, setCourbePng] = useState<string>();
  const [courbeHabitudesPng, setCourbeHabitudesPng] = useState<string>();

  useEffect(() => {
    let isStillMounted = true;
    (async () => {
      if (patient && user && isStillMounted && courbePng && courbeHabitudesPng) {
        setGenerating(true);

        const trueSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24" style="top:7px;position:relative;"><path fill="green" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z"></path></svg>`;
        const falseSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24" style="top:7px;position:relative;"><path fill="red" d="M3 3v18h18V3zm14 12.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12z"></path></svg>`;

        let antecedant = t('Non');
        let hadAlready = false;
        if (patient.oeils) {
          patient.oeils.reduce((acc, oeil) => {
            if (hadAlready && oeil.antecedantChirurgie === '1') {
              antecedant = t('ODG');
              hadAlready = true;
            }
            if (!hadAlready && oeil.antecedantChirurgie === '1') {
              antecedant = oeil.side === 'left' ? t('OG') : t('OD');
              hadAlready = true;
            }
            return acc;
          }, '');
        }

        let antecedantCristallin = t('Non');
        hadAlready = false;
        if (patient.oeils) {
          patient.oeils.reduce((acc, oeil) => {
            if (hadAlready && oeil.antecedantChirurgieCristallin === '1') {
              antecedantCristallin = t('ODG');
              hadAlready = true;
            }
            if (!hadAlready && oeil.antecedantChirurgieCristallin === '1') {
              antecedantCristallin = oeil.side === 'left' ? t('OG') : t('OD');
              hadAlready = true;
            }
            return acc;
          }, '');
        }

        let decalage: string | false = false;
        if (patient.oeils) {
          patient.oeils.reduce((acc, oeil) => {
            if (oeil.choixes[0]?.decalage > 0) {
              decalage =
                t('Décalage') +
                ' : ' +
                oeil.choixes[0].decalage +
                ' δ, ' +
                (oeil.side === 'left' ? t('OG') : t('OD')) +
                ' ' +
                t('dominé');
              hadAlready = true;
            }
            return acc;
          }, '');
        }

        let implantOD, implantOG, OG, OD;
        if (patient.oeils) {
          OD = patient.oeils.find((oeil) => oeil.side === 'right');
          implantOD = OD?.choixes[0]?.implant;
          OG = patient.oeils.find((oeil) => oeil.side === 'left');
          implantOG = OG?.choixes[0]?.implant;
          if (!OG?.choixes[0]?.final) {
            implantOG = implantOD;
          }
          if (!OD?.choixes[0]?.final) {
            implantOD = implantOG;
          }
          if (OD?.aOperer != '1') {
            implantOD = undefined;
          }
          if (OG?.aOperer != '1') {
            implantOG = undefined;
          }
        }

        const infoLunettes = [];
        if (patient.lunettesVL) {
          infoLunettes.push(t('Vision de loin'));
        }
        if (patient.lunettesVI) {
          infoLunettes.push(t('Vision intermédiaire'));
        }
        if (patient.lunettesVP) {
          infoLunettes.push(t('Vision de près'));
        }

        let chirurgien = '';
        if (user.logo) {
          chirurgien += `<img class="chirurgien-logo" src="${user.logo}" /><br/>`;
        }
        const userTitle = getTitleForLanguage(userTitles, i18n.language);
        const titlePrefix = i18n.language === 'fr' ? `${userTitle} ` : '';
        const titleSuffix = i18n.language === 'fr' ? '' : ` ${userTitle}`;
        chirurgien += `<span class="font-bold">${titlePrefix}${user?.prenom} ${user?.nom}${titleSuffix}</span><br/>`;
        if (user.clinique) {
          chirurgien += `${user.clinique}<br/>`;
        }
        if (user.adresse) {
          chirurgien += `${user.adresse}<br/>`;
        }
        if (user.codePostal) {
          chirurgien += `${user.codePostal} `;
        }
        if (user.ville) {
          chirurgien += `${user.ville}`;
        }

        const replacementValues: { [key: string]: string } = {
          chirurgien,
          age: DateUtils.getAge(patient.birthday as string),
          size: patient.size,
          sexe: patient.sexe === 1 ? t('Masculin') : t('Féminin'),
          antecedant,
          antecedantCristallin,
          lunettes: patient.glasses ? falseSvg : trueSvg,
          photiques: patient.photicEffect ? trueSvg : falseSvg,
          typeImplant:
            implantOD && implantOG
              ? implantOD.type !== implantOG.type
                ? implantOD.type + ' / ' + implantOG.type
                : implantOD.type
              : implantOD?.type || implantOG?.type || '',
          materiau:
            implantOD && implantOG
              ? implantOD.materiau !== implantOG.materiau
                ? t(implantOD.materiau) + ' / ' + t(implantOG.materiau)
                : t(implantOD.materiau)
              : implantOD?.materiau || implantOG?.materiau || '',
          implant:
            implantOD && implantOG
              ? implantOD.name !== implantOG.name
                ? `${t('OD')} ${implantOD.name} (${implantOD.laboratoire.nom}) / ${t('OG')} ${
                    implantOG.name
                  } (${implantOG.laboratoire.nom})`
                : `${t('ODG')} ${implantOD.name} (${implantOD.laboratoire.nom})`
              : implantOD
                ? `${t('OD')} ${implantOD.name} (${implantOD.laboratoire.nom})`
                : `${t('OG')} ${implantOG?.name} (${implantOG?.laboratoire.nom})`,
          larmesOD: 'red',
          monovision:
            patient.monovision && decalage !== false ? t('Oui') + ', ' + decalage : t('Non'),
          infoLunettes:
            (infoLunettes.length > 0 ? infoLunettes.join(', ') : `${t('Non')}`) +
            `<br/><span style="color:grey;">${t(
              'Après la chirurgie, une correction visuelle complémentaire pourra être nécessaire pour certaines activités.'
            )}</span>`,
        };

        Object.keys(parametersData).map((parameter) => {
          //oeil gauche
          replacementValues[parameter + 'OG'] = parametersData[parameter].find(
            (option) => option.value === OG[parameter]
          )?.activeColor;
          //oeil droit
          replacementValues[parameter + 'OD'] = parametersData[parameter].find(
            (option) => option.value === OD[parameter]
          )?.activeColor;
        });

        replacementValues.habitudesVisuelles = `<img src="${courbeHabitudesPng}"/>`;
        replacementValues.courbes = `<img src="${courbePng}"/>`;

        const html = Object.entries(replacementValues).reduce(
          (acc, [key, value]) => {
            return acc.replace(new RegExp(`{{${key}}}`, 'g'), value);
          },
          i18n.language !== 'fr' ? TemplateRapport_EN : TemplateRapport_FR
        );

        const result = await pdfGenerator(html);
        if (isStillMounted && result !== false) {
          setPdfData(result.uri);
          setGenerating(false);
        }
      }
    })();
    return () => {
      isStillMounted = false;
    };
  }, [
    patient,
    courbePng,
    courbeHabitudesPng,
    JSON.stringify(user),
    i18n.language,
    userTitles.titreFr,
    userTitles.titreEn,
  ]);

  useEffect(() => {
    setCourbeHabitudesPng('');
    setCourbePng('');
  }, [JSON.stringify(patient)]);

  let implantOD, implantOG, OG, OD;
  if (patient?.oeils) {
    OD = patient.oeils.find((oeil) => oeil.side === 'right');
    implantOD = OD?.choixes[0]?.implant;
    OG = patient.oeils.find((oeil) => oeil.side === 'left');
    implantOG = OG?.choixes[0]?.implant;
    if (!OG?.choixes[0]?.final) {
      implantOG = implantOD;
    }
    if (!OD?.choixes[0]?.final) {
      implantOD = implantOG;
    }
    if (OD?.aOperer != '1') {
      implantOD = undefined;
    }
    if (OG?.aOperer != '1') {
      implantOG = undefined;
    }
  }

  const graphCourbesOptions = getImplantChartOptions(
    patient,
    implantOG,
    implantOD,
    { 0: OG.choixes[0]?.decalage, 1: OD.choixes[0]?.decalage },
    patient?.monovision,
    patient?.oeilDominant,
    1345,
    t,
    false,
    {
      OD: getCourbePatient(OD, OD?.choixes[0]?.affichageDefocus === 1),
      OG: getCourbePatient(OG, OG?.choixes[0]?.affichageDefocus === 1),
    },
    {
      OD: -OD.choixes[0]?.decalageCourbeExistante,
      OG: -OG.choixes[0]?.decalageCourbeExistante,
    }
  );

  const graphCourbesHabitudesOptions = getHabitudeChartOptions(
    patient,
    t,
    '25%',
    '43%',
    'bold',
    16
  );

  return (
    <View style={{ flex: 1 }}>
      {!courbePng ? (
        <EChartsCaptureComponent
          chartOptions={graphCourbesOptions}
          width={1345 / 1.5}
          height={400 / 1.5}
          onCapture={(data) => setCourbePng(data)}
        />
      ) : null}
      {!courbeHabitudesPng && courbePng ? (
        <EChartsCaptureComponent
          chartOptions={graphCourbesHabitudesOptions}
          width={300}
          height={300}
          onCapture={(data) => setCourbeHabitudesPng(data)}
        />
      ) : null}
      {generating || !pdfData ? (
        <CenteredLoading />
      ) : (
        <>
          <View
            style={{ flex: 1 }}
            onLayout={(event) => {
              if (
                event.nativeEvent.layout.width !== currentLayout?.width ||
                event.nativeEvent.layout.height !== currentLayout?.height
              ) {
                setCurrentLayout({
                  width: event.nativeEvent.layout.width,
                  height: event.nativeEvent.layout.height,
                });
              }
            }}
          >
            <Pdf
              url={pdfData}
              width={currentLayout?.width}
              height={currentLayout?.height}
              fileName={'rapport.pdf'}
            />
          </View>
          <View style={{ padding: 20, justifyContent: 'space-around', flexDirection: 'row' }}>
            <SimpleLink
              onPress={async () => {
                const directory = `${FileSystem.cacheDirectory}rapports/temp/`;
                console.log(directory);
                await FileSystem.makeDirectoryAsync(directory, { intermediates: true });
                console.log('aa');
                try {
                  await FileSystem.copyAsync({
                    from: pdfData as string,
                    to: directory + 'Rapport.pdf',
                  });
                } catch (error) {
                  console.log(error);
                }
                console.log('bb');
                try {
                  await Sharing.shareAsync(directory + 'Rapport.pdf');
                  await FileSystem.deleteAsync(directory + 'Rapport.pdf');
                } catch (error) {
                  console.log(error);
                }
                console.log('cc');
              }}
            >
              Partager
            </SimpleLink>
            <SimpleLink
              onPress={async () => {
                await Print.printAsync({ uri: pdfData });
              }}
            >
              Imprimer
            </SimpleLink>
          </View>
        </>
      )}
    </View>
  );
};

export default RapportPage;
